/* eslint-disable react/jsx-props-no-spreading,max-len */
import {
    useCallback,
} from 'react'

import {
    ColumnHeaderFilterIconButtonProps,
    useGridApiContext,
    useGridRootProps,
    useGridSelector,
    gridPreferencePanelStateSelector,
    gridFilterableColumnDefinitionsSelector,
} from '@mui/x-data-grid-pro'
import {
    unstable_useId as useId,
} from '@mui/material'

import useStyles from './FilterIcon.styles'

/* Custom Filter column Icon component
* to be able to show filter icon next to sort icon on hover */
function FilterIcon(props: Readonly<ColumnHeaderFilterIconButtonProps>) {
    const {
        classes,
    } = useStyles()
    const {
        counter = 0, field, onClick,
    } = props
    const apiRef = useGridApiContext()
    const rootProps = useGridRootProps()
    const preferencePanel = useGridSelector(
        apiRef,
        gridPreferencePanelStateSelector,
    )
    const filterableColumnDefinitions = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector)
    const labelId = useId()
    const panelId = useId()

    const toggleFilter = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault()
            event.stopPropagation()

            apiRef.current.showFilterPanel(field, panelId, labelId)

            if (onClick) {
                onClick(apiRef.current.getColumnHeaderParams(field), event)
            }
        },
        [
            apiRef,
            field,
            onClick,
            panelId,
            labelId,
        ],
    )

    const open = preferencePanel.open && preferencePanel.labelId === labelId
    const isFilterable = Boolean(filterableColumnDefinitions.find((item) => {
        return item.field === field
    }))
    const hasActiveFilter = counter > 0

    if (!isFilterable) {
        return null
    }

    const iconButton = (
        <rootProps.slots.baseIconButton
            id={labelId}
            onClick={toggleFilter}
            color="default"
            aria-label={apiRef.current.getLocaleText('columnHeaderFiltersLabel')}
            size="small"
            tabIndex={-1}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            className={`${hasActiveFilter ? classes.activeFilter : classes.hoverOnly}`}
            {...rootProps.slotProps?.baseIconButton}
        >
            <rootProps.slots.columnFilteredIcon
                fontSize="small"
                className={counter < 1 ? classes.empty : undefined}
            />
        </rootProps.slots.baseIconButton>
    )

    return (
        <rootProps.slots.baseTooltip
            title={
                apiRef.current.getLocaleText('columnHeaderFiltersTooltipActive')(
                    counter,
                ) as React.ReactElement
            }
            enterDelay={1000}
            {...rootProps.slotProps?.baseTooltip}
        >
            {iconButton}
        </rootProps.slots.baseTooltip>
    )
}

export default FilterIcon
